/**
 * Grid Column Utilities
 *
 * Generic utilities for preparing MUI DataGrid columns from column definitions.
 * Works with any column configuration file (employeeUtilities.js, allowanceUtilities.js, etc.)
 *
 * Column definitions should have:
 * - value: field name (required)
 * - type: TYPE_TEXT, TYPE_DATE, TYPE_NUMBER, TYPE_BOOLEAN, TYPE_DECIMAL (required)
 * - label: translation key (required)
 * - field: FIELD_USER, FIELD_EMPLOYEE, FIELD_ENUM (optional)
 * - reference: reference table UUID (optional)
 * - labelColumn: column name for display in reference table (optional, default: 'Omschrijving')
 * - valueColumn: column name for value in reference table (optional, default: 'Code')
 * - getCellValue: custom value getter function (optional)
 * - formatter: 'currency', 'date', 'percentage' (optional)
 * - isArrayField: true if field contains array values (optional)
 * - enrichmentField: name of field containing enriched data (optional, e.g., 'stationDetails')
 * - renderComponent: custom component name for rendering (optional, e.g., 'Country')
 * - mutedWhenInactive: apply opacity to inactive items (optional, default: true for all fields)
 * - width: fixed column width (optional)
 * - sortable: allow sorting (optional, default: true)
 * - filterable: allow filtering (optional, default: true)
 */

import React from 'react'
import { Box } from '@mui/material'
import { getGridStringOperators, getGridNumericOperators, getGridSingleSelectOperators } from '@mui/x-data-grid-premium'
import { TYPE_DATE, TYPE_NUMBER, TYPE_BOOLEAN, TYPE_DECIMAL } from '../constants/types'
import { FIELD_USER } from '../constants/fields'
import { getFullName as getFullUserName, getUserSelectOptions } from './userUtilities'
import { createRelativeDateOperators } from './relativeDateOperators'
import { createBooleanOperators, renderBooleanIcon } from './booleanOperators'

/**
 * Get value from nested field path
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((val, key) => val?.[key], obj)
}

/**
 * Create safe string filter operators that handle null/undefined values
 */
const createSafeStringOperators = () => {
  return getGridStringOperators().map(operator => {
    // Override getApplyFilterFn to handle null/undefined
    const originalGetApplyFilterFn = operator.getApplyFilterFn

    return {
      ...operator,
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) {
          return null
        }

        const originalFn = originalGetApplyFilterFn ? originalGetApplyFilterFn(filterItem) : null
        if (!originalFn) {
          return null
        }

        // Wrap the original function to handle null/undefined cell values
        return (params) => {
          const cellValue = params.value
          // Return false for null/undefined values instead of throwing error
          if (cellValue === null || cellValue === undefined) {
            return false
          }
          // Convert to string if not already
          const stringValue = String(cellValue)
          // Create new params with string value
          return originalFn({ ...params, value: stringValue })
        }
      }
    }
  }).filter(op => op.value !== 'startsWith' && op.value !== 'endsWith')
}

/**
 * Get enriched data field name (e.g., 'station' -> 'stationDetails')
 */
const getEnrichmentFieldName = (columnDef) => {
  if (columnDef.enrichmentField) {
    return columnDef.enrichmentField
  }
  // Default pattern: append 'Details' to the last part of the field path
  const fieldParts = columnDef.value.split('.')
  const lastPart = fieldParts[fieldParts.length - 1]
  return fieldParts.slice(0, -1).concat(lastPart + 'Details').join('.')
}

/**
 * Create value getter for a column
 */
const createValueGetter = (columnDef, options) => {
  const { dataTables = {} } = options
  const field = columnDef.value

  // Reference fields with enrichment
  if (columnDef.reference) {
    const referenceTable = dataTables[columnDef.reference]
    const labelColumn = columnDef.labelColumn || 'Omschrijving'
    const valueColumn = columnDef.valueColumn || 'Code'

    return (params) => {
      const rawValue = getNestedValue(params.row, field)

      // Handle array fields (like country)
      if (columnDef.isArrayField && Array.isArray(rawValue)) {
        if (rawValue.length === 0) return null

        // Try enrichment field first (e.g., countryDetails)
        const enrichmentField = getEnrichmentFieldName(columnDef)
        const enrichedData = getNestedValue(params.row, enrichmentField)

        if (enrichedData && Array.isArray(enrichedData)) {
          return enrichedData.map(item => item[labelColumn]).filter(Boolean).join(', ')
        }

        // Fallback: enrich using reference table
        if (referenceTable?.records) {
          return rawValue
            .map(code => {
              const record = referenceTable.records.find(r => r[valueColumn] === code)
              return record?.[labelColumn] || code
            })
            .join(', ')
        }

        return rawValue.join(', ')
      }

      if (!rawValue) return null

      // Try enrichment field first (e.g., stationDetails)
      const enrichmentField = getEnrichmentFieldName(columnDef)
      const enrichedData = getNestedValue(params.row, enrichmentField)

      if (enrichedData) {
        // Return the label column value
        return enrichedData[labelColumn] || enrichedData[valueColumn] || rawValue
      }

      // Fallback: use reference table to enrich
      if (referenceTable?.records) {
        const record = referenceTable.records.find(r => r[valueColumn] === rawValue)
        return record?.[labelColumn] || rawValue
      }

      return rawValue
    }
  }

  // Custom getCellValue function
  if (columnDef.getCellValue) {
    return (params) => columnDef.getCellValue(params.row)
  }

  // Simple fields - direct access
  return (params) => getNestedValue(params.row, field)
}

/**
 * Create value formatter for a column
 */
const createValueFormatter = (columnDef, options) => {
  const { allUsers = {} } = options
  const columnType = columnDef.type

  // User fields: format user name
  if (columnDef.field === FIELD_USER) {
    return (params) => {
      if (!params.value) return ''
      const user = allUsers[params.value]
      if (!user) return params.value
      return getFullUserName(user)
    }
  }

  // Currency formatter
  if (columnDef.formatter === 'currency' || columnType === TYPE_DECIMAL) {
    return (params) => {
      if (!params.value) return ''
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR'
      }).format(params.value)
    }
  }

  // Date formatter
  if (columnDef.formatter === 'date' || columnType === TYPE_DATE) {
    return (params) => {
      if (!params.value) return ''
      return new Date(params.value).toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
  }

  // Percentage formatter
  if (columnDef.formatter === 'percentage') {
    return (params) => {
      if (!params.value) return ''
      return new Intl.NumberFormat('nl-NL', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(params.value / 100)
    }
  }

  // Reference fields are already formatted by valueGetter
  // No additional formatting needed
  return null
}

/**
 * Create render cell function for special rendering
 */
const createRenderCell = (columnDef, options) => {
  const { customComponents = {} } = options
  const mutedWhenInactive = columnDef.mutedWhenInactive !== false // default true

  return (params) => {
    const isMuted = mutedWhenInactive && !params.row.activeApprovedOffset?.active
    const displayValue = params.formattedValue || params.value || ''

    // Custom component rendering (e.g., Country for station)
    if (columnDef.renderComponent && customComponents[columnDef.renderComponent]) {
      const CustomComponent = customComponents[columnDef.renderComponent]
      return (
        <Box sx={{ opacity: isMuted ? 0.5 : 1 }}>
          <CustomComponent
            value={displayValue}
            rawValue={params.row[columnDef.value]}
            row={params.row}
          />
        </Box>
      )
    }

    // Boolean rendering with icons
    if (columnDef.type === TYPE_BOOLEAN) {
      return (
        <Box sx={{ opacity: isMuted ? 0.5 : 1 }}>
          {renderBooleanIcon(params.value, { muted: isMuted })}
        </Box>
      )
    }

    // Default rendering with optional muting
    return (
      <Box sx={{ opacity: isMuted ? 0.5 : 1 }}>
        {displayValue}
      </Box>
    )
  }
}

/**
 * Create value options for select dropdowns
 */
const createValueOptions = (columnDef, options) => {
  const { allUsers = {}, dataTables = {} } = options

  // User fields
  if (columnDef.field === FIELD_USER && allUsers && Object.keys(allUsers).length > 0) {
    return getUserSelectOptions(allUsers)
  }

  // Reference fields
  if (columnDef.reference) {
    const referenceTable = dataTables[columnDef.reference]
    const labelColumn = columnDef.labelColumn || 'Omschrijving'
    const valueColumn = columnDef.valueColumn || 'Code'

    if (referenceTable?.records) {
      return referenceTable.records
        .filter(record => record && record[valueColumn])
        .map(record => ({
          value: record[valueColumn],
          label: record[labelColumn] || record[valueColumn]
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }
  }

  return null
}

/**
 * Create filter operators for array fields
 */
const createArrayFieldOperators = (columnDef, options) => {
  const { intl } = options
  const singleSelectOperators = getGridSingleSelectOperators()

  return singleSelectOperators
    .filter(op => op.value === 'isAnyOf')
    .map(op => ({
      ...op,
      label: intl ? intl.formatMessage({ id: 'label.operator_isAnyOf' }) : 'Is any of',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value || filterItem.value.length === 0) {
          return null
        }
        return (params) => {
          const cellValue = getNestedValue(params.row, columnDef.value) || []
          if (!Array.isArray(cellValue)) return false
          // Check if any selected value is in the cell's array
          return filterItem.value.some(selectedValue => cellValue.includes(selectedValue))
        }
      }
    }))
}

/**
 * Prepare columns for MUI DataGrid from column definitions
 *
 * @param {Array} columnDefinitions - Array of column definitions (e.g., from employeeUtilities.js)
 * @param {Object} options - Additional options
 * @param {Object} options.allUsers - Object with user IDs as keys (for user fields)
 * @param {Object} options.dataTables - Data tables for reference fields (keyed by table UUID)
 * @param {Object} options.intl - Intl object for translations
 * @param {Object} options.customComponents - Custom components for rendering (e.g., { Country: CountryComponent })
 * @param {Function} options.getInactiveStatus - Function to determine if row is inactive (optional)
 * @returns {Array} Prepared columns for MUI DataGrid
 */
export const prepareColumns = (columnDefinitions, options = {}) => {
  const { intl } = options

  if (!columnDefinitions) {
    return []
  }

  return columnDefinitions.map(columnDef => {
    const field = columnDef.value
    const columnType = columnDef.type

    // Base column configuration
    const column = {
      field,
      headerName: intl ? intl.formatMessage({ id: columnDef.label }) : columnDef.label,
      width: columnDef.width || 150,
      flex: columnDef.width ? undefined : 1,
      sortable: columnDef.sortable !== false,
      filterable: columnDef.filterable !== false,
      align: 'left',
      headerAlign: 'left',
    }

    // Create value getter
    const valueGetter = createValueGetter(columnDef, options)
    if (valueGetter) {
      column.valueGetter = valueGetter
    }

    // Create value formatter
    const valueFormatter = createValueFormatter(columnDef, options)
    if (valueFormatter) {
      column.valueFormatter = valueFormatter
    }

    // Create value options for select fields
    const valueOptions = createValueOptions(columnDef, options)

    // Configure filter operators based on column type
    let filterOperators

    // User fields and reference fields with valueOptions should be treated as singleSelect
    // Check this BEFORE checking TYPE_TEXT to avoid regression
    if (valueOptions && valueOptions.length > 0) {
      column.type = 'singleSelect'
      column.valueOptions = valueOptions

      // Array fields need special filter operators
      if (columnDef.isArrayField) {
        filterOperators = createArrayFieldOperators(columnDef, options)
      } else {
        filterOperators = getGridSingleSelectOperators()
      }
    }
    else if (columnType === TYPE_DATE) {
      column.type = 'date'
      filterOperators = createRelativeDateOperators(intl)
    }
    else if (columnType === TYPE_NUMBER || columnType === TYPE_DECIMAL) {
      column.type = 'number'
      filterOperators = getGridNumericOperators()
    }
    else if (columnType === TYPE_BOOLEAN) {
      column.type = 'boolean'
      filterOperators = createBooleanOperators(intl)
    }
    else {
      // Default to string
      column.type = 'string'
      filterOperators = createSafeStringOperators()
    }

    if (filterOperators) {
      column.filterOperators = filterOperators
    }

    // Create render cell function
    column.renderCell = createRenderCell(columnDef, options)

    return column
  })
}

/**
 * Get filter field mappings from column definitions
 * This helps map display fields to their actual database fields for filtering
 *
 * @param {Array} columnDefinitions - Array of column definitions
 * @returns {Object} Map of field names to their filter field configurations
 */
export const getFilterFieldMappings = (columnDefinitions) => {
  const mappings = {}

  columnDefinitions.forEach(columnDef => {
    const field = columnDef.value

    mappings[field] = {
      filterField: field,
      isUserField: columnDef.field === FIELD_USER,
      isReferenceField: Boolean(columnDef.reference),
      isArrayField: columnDef.isArrayField || false,
      hasCustomGetter: Boolean(columnDef.getCellValue),
      type: columnDef.type
    }
  })

  return mappings
}
